// srccomponents/LanguageSwitcher.tsx
import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { StoreContext } from "../context/StoreContext";
import {
  SUPPORTED_PLATFORM_LANGUAGES,
  getLanguageByCode,
} from "../utils/languageConfig";

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  // Safely access store context - returns undefined if provider is not available (platform pages)
  const storeContext = useContext(StoreContext);
  const store = storeContext?.store;
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  // Check if we're on the setup wizard page
  const isWizardPage = location.pathname === "/admin/setup-wizard";

  // Get supported languages from store, default to supported platform languages if store context is not available
  // In the wizard, always show all supported platform languages regardless of store settings
  const supportedLanguages = useMemo(() => {
    // If on wizard page, always show all supported platform languages
    if (isWizardPage) {
      return SUPPORTED_PLATFORM_LANGUAGES;
    }

    // If store context is not available (platform pages), show all supported platform languages
    if (!storeContext) {
      return SUPPORTED_PLATFORM_LANGUAGES;
    }
    // If store context exists but store is not loaded yet, return empty array (will hide switcher)
    if (!store) {
      return [];
    }

    // Get the raw supportedLanguages from store
    const rawSupportedLanguages = store.supportedLanguages;

    // Ensure supportedLanguages is always an array, default to ["en"] if undefined/null/empty
    let storeLanguages: string[];
    if (
      Array.isArray(rawSupportedLanguages) &&
      rawSupportedLanguages.length > 0
    ) {
      storeLanguages = rawSupportedLanguages;
    } else {
      // Default to English only if not set
      storeLanguages = ["en"];
    }

    // Filter to only include languages that are in the store's supported languages AND are supported by the platform
    // Map store language codes to full language objects
    const filtered = storeLanguages
      .map((code) => getLanguageByCode(code))
      .filter(
        (lang): lang is (typeof SUPPORTED_PLATFORM_LANGUAGES)[0] =>
          lang !== undefined &&
          SUPPORTED_PLATFORM_LANGUAGES.some(
            (supported) => supported.code === lang.code
          )
      );

    // If no valid languages found, default to English
    return filtered.length > 0 ? filtered : [SUPPORTED_PLATFORM_LANGUAGES[0]];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isWizardPage,
    location.pathname,
    storeContext,
    store?.supportedLanguages,
    store?._id,
    store?.name,
    store,
  ]);

  // If only one language is supported and we have a store context, ensure it's set and don't render switcher
  useEffect(() => {
    if (supportedLanguages.length === 1 && storeContext && store) {
      const singleLanguage = supportedLanguages[0].code;
      if (i18n.language !== singleLanguage) {
        i18n.changeLanguage(singleLanguage);
      }
    }
  }, [supportedLanguages, storeContext, store, i18n]);

  // Handle click outside dropdown to close it
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Don't render if store context is available but store is not loaded yet (to avoid flickering)
  if (storeContext && !store) {
    return null;
  }

  // Don't render if no supported languages (shouldn't happen, but safety check)
  if (supportedLanguages.length === 0) {
    return null;
  }

  // Don't render if only one language is supported and we have a store context
  if (supportedLanguages.length <= 1 && storeContext && store) {
    return null;
  }

  const changeLanguage = (lang: string): void => {
    i18n.changeLanguage(lang);
    // Language persistence and direction are handled by i18n listener in i18n.ts
    setOpen(false);
  };

  // Get current language code (extract base code from i18n.language which might be "en-US" -> "en")
  const currentLanguageCode = (i18n.language || "en")
    .split("-")[0]
    .toLowerCase();
  const currentLanguage =
    supportedLanguages.find((l) => l.code === currentLanguageCode) ||
    supportedLanguages[0];

  // If only 2 languages, show as simple link button
  if (supportedLanguages.length === 2) {
    const otherLanguage = supportedLanguages.find(
      (lang) => lang.code !== currentLanguage.code
    );
    if (!otherLanguage) return null;

    return (
      <button
        className="flex items-center gap-2 px-3 py-2 border border-gray-200 bg-white hover:bg-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors"
        onClick={() => changeLanguage(otherLanguage.code)}
        aria-label={`Switch to ${otherLanguage.label}`}
      >
        <span className="text-lg">{otherLanguage.flag}</span>
        <span className="hidden sm:inline font-medium">
          {otherLanguage.nativeName}
        </span>
      </button>
    );
  }

  // Calculate dropdown position - open upward if near bottom of viewport
  // Also account for chat widget in bottom-right corner
  const getDropdownPosition = (): {
    top: string;
    right: string;
    openUpward: boolean;
  } => {
    if (!buttonRef.current) {
      return { top: "0", right: "0", openUpward: false };
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const dropdownHeight = supportedLanguages.length * 40 + 16; // Approximate height (40px per item + padding)
    const gap = 8;

    // Chat widget dimensions: ~56px (button) or ~400px (open widget) + margin
    // On desktop: bottom-24 (96px), on mobile: bottom-4 (16px)
    const chatWidgetWidth = 400; // Max width when open
    const isDesktop = viewportWidth >= 768; // md breakpoint
    const chatWidgetBottom = isDesktop ? 96 : 16; // bottom-24 on desktop, bottom-4 on mobile
    const chatWidgetMargin = 16; // right-4 = 16px
    const chatWidgetAreaRight = chatWidgetWidth + chatWidgetMargin;

    // Check if button is in the chat widget area (bottom-right)
    const buttonRight = viewportWidth - buttonRect.right;
    const buttonBottom = viewportHeight - buttonRect.bottom;
    // Check if button is within chat widget's horizontal area and near the bottom
    // On mobile, language switcher is centered, so less likely to overlap
    const isNearChatWidget =
      buttonRight < chatWidgetAreaRight && buttonBottom < chatWidgetBottom + 60;

    // Calculate right position to align dropdown's right edge with button's right edge
    // right = distance from viewport's right edge to button's right edge
    let rightPosition: number;
    if (isNearChatWidget) {
      // Position dropdown to the left of chat widget area, but still align with button
      // Keep the alignment with button's right edge
      rightPosition = viewportWidth - buttonRect.right;
      // Ensure minimum margin from right edge
      if (rightPosition < 8) {
        rightPosition = 8;
      }
    } else {
      // Align dropdown's right edge with button's right edge
      rightPosition = viewportWidth - buttonRect.right;
      // Ensure minimum margin from right edge
      if (rightPosition < 8) {
        rightPosition = 8;
      }
    }

    // Open upward if not enough space below but enough space above
    const openUpward =
      spaceBelow < dropdownHeight + gap && spaceAbove > dropdownHeight + gap;

    if (openUpward) {
      return {
        top: `${buttonRect.top - dropdownHeight - gap}px`,
        right: `${rightPosition}px`,
        openUpward: true,
      };
    } else {
      return {
        top: `${buttonRect.bottom + gap}px`,
        right: `${rightPosition}px`,
        openUpward: false,
      };
    }
  };

  // Calculate position only when dropdown is open
  const dropdownPosition = open
    ? getDropdownPosition()
    : { top: "0", right: "0", openUpward: false };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        className="flex items-center gap-2 px-3 py-2 border border-gray-200 bg-white hover:bg-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
      >
        {currentLanguage.flag || "🌐"}
        <span className="hidden sm:inline font-medium">
          {currentLanguage.nativeName || currentLanguage.code}
        </span>
        <svg
          className={`w-3 h-3 ml-1 transition-transform ${open && !dropdownPosition.openUpward ? "rotate-180" : "rotate-0"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open &&
        buttonRef.current &&
        createPortal(
          <ul
            ref={dropdownRef}
            className="fixed bg-white border border-gray-200 z-[100] py-1 min-w-[8rem]"
            role="listbox"
            style={{
              top: dropdownPosition.top,
              right: dropdownPosition.right,
              animation: dropdownPosition.openUpward
                ? "fadeInSlideUp 0.2s ease-out"
                : "fadeInSlideDown 0.2s ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {supportedLanguages.map((lang) => (
              <li
                key={lang.code}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-colors duration-150 hover:bg-blue-50 ${
                  currentLanguage.code === lang.code
                    ? "font-bold text-blue-600 bg-blue-100"
                    : "text-gray-700"
                }`}
                onClick={() => changeLanguage(lang.code)}
                role="option"
                aria-selected={currentLanguage.code === lang.code}
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter") changeLanguage(lang.code);
                }}
              >
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.nativeName}</span>
                {currentLanguage.code === lang.code && (
                  <svg
                    className="w-3 h-3 ml-auto text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default LanguageSwitcher;
